import { cn } from "@/lib/utils";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { type SectionTier, strictWinners } from "@repo/shared/hooks";
import { useTranslation } from "@repo/shared/i18n";
import { comparePlayerKey } from "@repo/shared/redux";
import { DetailedPlayer } from "@repo/shared/types";
import {
  ATTRIBUTE_CATEGORIES,
  FACE_STAT_KEYS,
  categoryToUniqueKey,
  inGameAttrsForCategory,
} from "@repo/shared/utils";
import { useSelector } from "react-redux";

import {
  CompareRow,
  CompareSectionShell,
  compareValueClass,
  zebra,
} from "./CompareSectionShell";
import { Mode } from "./ModeToggle";

/**
 * Compact mode: one header row with the six abbreviated overalls (value under
 * the label), then one dense row per category — that category's in-game
 * attributes abbreviated, flex-wrapped per player. No category header rows.
 */
const CompactMode = ({
  players,
  gridStyle,
}: {
  players: DetailedPlayer[];
  gridStyle: React.CSSProperties;
}) => {
  const { playerAttributes } = useSelector(contentLabelsSelector);
  const categories = ATTRIBUTE_CATEGORIES.map(({ category }) => ({
    category,
    rows: inGameAttrsForCategory(category).filter(({ key }) =>
      players.some((p) => p.inGameAttributes?.[key] != null),
    ),
  })).filter(({ rows }) => rows.length > 0);
  const overallWinners = ATTRIBUTE_CATEGORIES.map((_, statIndex) =>
    strictWinners(players.map((p) => p.attributes[statIndex])),
  );
  const rowWinners = new Map(
    categories
      .flatMap(({ rows }) => rows)
      .map(({ key }) => [
        key,
        strictWinners(players.map((p) => p.inGameAttributes?.[key])),
      ]),
  );
  return (
    <>
      <div
        className="grid border-b border-neutral-800 bg-[#181818] py-[8px]"
        style={gridStyle}
      >
        {players.map((player, playerIdx) => (
          <div
            key={comparePlayerKey(player)}
            className="flex flex-wrap justify-center gap-x-1 gap-y-[4px] px-2"
          >
            {ATTRIBUTE_CATEGORIES.map(({ short }, statIndex) => {
              const value = player.attributes[statIndex];
              const faceShort =
                playerAttributes[FACE_STAT_KEYS.outfield[statIndex]]?.short ??
                short;
              return (
                // Fixed cell width so the label+value pairs line up across
                // rows; long labels may overflow the cell slightly.
                <div
                  key={short}
                  className="flex w-10 flex-none flex-col items-center"
                >
                  <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.05em] text-neutral-500">
                    {faceShort}
                  </span>
                  <span
                    className={cn(
                      "text-xs",
                      compareValueClass(
                        value,
                        overallWinners[statIndex].has(playerIdx),
                      ),
                    )}
                  >
                    {value}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {categories.map(({ category, rows }, catIndex) => (
        <div
          key={category}
          className={cn(
            "grid border-b border-neutral-800 py-[6px]",
            zebra(catIndex),
          )}
          style={gridStyle}
        >
          {players.map((player, playerIdx) => (
            <div
              key={comparePlayerKey(player)}
              className="flex flex-wrap content-start justify-center gap-x-1 mobile:gap-x-1 gap-y-[4px] px-2"
            >
              {rows.map(({ key, short }) => {
                const value = player.inGameAttributes?.[key];
                return (
                  // Same fixed width as the overalls row above, so every
                  // compact row shares one column rhythm.
                  <div
                    key={key}
                    className="flex w-10 flex-none flex-col items-center"
                  >
                    <span className="whitespace-nowrap text-[10px] font-medium uppercase text-neutral-500">
                      {playerAttributes[key]?.short ?? short}
                    </span>
                    <span
                      className={cn(
                        "text-xs",
                        compareValueClass(
                          value,
                          rowWinners.get(key)?.has(playerIdx) ?? false,
                        ),
                      )}
                    >
                      {value ?? "–"}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </>
  );
};

export const AttributesCompareSection = ({
  players,
  gridStyle,
  tiers,
  open,
  onToggle,
  mode,
  pinnedWidth,
}: {
  players: DetailedPlayer[];
  gridStyle: React.CSSProperties;
  /** Per-player `igAttributes` tier + winner from the comparison endpoint. */
  tiers?: SectionTier[];
  open: boolean;
  onToggle: () => void;
  mode: Mode;
  /** Mobile: sticky-left width for the section title in the shared scroller. */
  pinnedWidth?: number;
}) => {
  const { playerAttributes } = useSelector(contentLabelsSelector);
  const { t } = useTranslation("compare");
  // In list mode, left-align cells (pl-30) so they line up under the
  // playstyle rows above; in grouped mode, center them. Label sits above the
  // value so all numbers align vertically.
  const cellAlign = "pl-[30px]";
  return (
    <CompareSectionShell
      title={t("categories.igAttributes")}
      count={players.length}
      gridStyle={gridStyle}
      tiers={tiers}
      open={open}
      onToggle={onToggle}
      mode={mode}
      pinnedWidth={pinnedWidth}
    >
      {mode === "compact" ? (
        <CompactMode players={players} gridStyle={gridStyle} />
      ) : (
        ATTRIBUTE_CATEGORIES.map(({ short, category }, statIndex) => {
          const overallValues = players.map((p) => p.attributes[statIndex]);
          const overallWinners = strictWinners(overallValues);
          // Only in-game rows at least one player has a value for (players
          // staged without inGameAttributes just show the overall row).
          const rows = inGameAttrsForCategory(category).filter(({ key }) =>
            players.some((p) => p.inGameAttributes?.[key] != null),
          );
          return (
            <div key={short}>
              {/* Overall row — the category title, playstyles-header style */}
              <div
                className="grid items-center border-t border-b border-neutral-500 bg-[#181818] py-[10px]"
                style={gridStyle}
              >
                {players.map((player, idx) => (
                  <div
                    key={comparePlayerKey(player)}
                    className={cn(
                      "flex flex-row items-center gap-x-2",
                      cellAlign,
                    )}
                  >
                    <span className="text-xs font-extrabold uppercase tracking-[0.1em] text-neutral-100">
                      {playerAttributes[categoryToUniqueKey[category]]?.label ??
                        category}
                    </span>
                    <span
                      className={cn(
                        "text-sm",
                        compareValueClass(
                          overallValues[idx],
                          overallWinners.has(idx),
                        ),
                      )}
                    >
                      {overallValues[idx]}
                    </span>
                  </div>
                ))}
              </div>
              {rows.map(({ key, label }, rowIndex) => {
                const values = players.map((p) => p.inGameAttributes?.[key]);
                const winners = strictWinners(values);
                return (
                  <CompareRow key={key} index={rowIndex} gridStyle={gridStyle}>
                    {players.map((player, idx) => {
                      const value = values[idx];
                      return (
                        <div
                          key={comparePlayerKey(player)}
                          className={cn(
                            cellAlign,
                            // Grouped: label + value share one line (label
                            // left, value right); list stacks them.
                            mode === "grouped"
                              ? "flex items-center justify-between gap-2 pr-[14px]"
                              : "flex flex-col gap-[2px]",
                          )}
                        >
                          <span className="text-xs font-medium text-neutral-500">
                            {playerAttributes[key]?.label ?? label}
                          </span>
                          <span
                            className={cn(
                              "text-xs",
                              compareValueClass(value, winners.has(idx)),
                            )}
                          >
                            {value ?? "–"}
                          </span>
                        </div>
                      );
                    })}
                  </CompareRow>
                );
              })}
            </div>
          );
        })
      )}
    </CompareSectionShell>
  );
};
